import React, { useState, useMemo, useEffect } from 'react';
import { Sparkles, Loader2, Save, Cpu, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { MODEL_OPTIONS } from '../lib/constants';
import NewsletterMarketEditor, { FIELDS } from './NewsletterMarketEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MARKETS = [
  { code: 'SE', name: 'Sverige',       flag: '🇸🇪' },
  { code: 'NO', name: 'Norge',         flag: '🇳🇴' },
  { code: 'FI', name: 'Finland',       flag: '🇫🇮' },
  { code: 'DK', name: 'Danmark',       flag: '🇩🇰' },
  { code: 'DE', name: 'Tyskland',      flag: '🇩🇪' },
  { code: 'NL', name: 'Nederländerna', flag: '🇳🇱' },
];

export default function NewsletterCreateTab() {
  const { session, user } = useAuth();
  const { addUsageLog } = useData();
  const accessToken = session?.access_token;

  // Hämta aktiva mallar för selektorn
  useEffect(() => {
    if (!accessToken) return;
    fetch(`${API_URL}/api/newsletter/templates`, {
      headers: { 'Authorization': `Bearer ${accessToken}` },
    })
      .then(r => r.ok ? r.json() : { templates: [] })
      .then(d => setTemplates(d.templates || []))
      .catch(() => {});
  }, [accessToken]);

  const [form, setForm] = useState({
    campaign_name: '',
    brief: '',
    cta_text: 'Shoppa nu',
    markets: ['SE'],
  });
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState('gemini');   // lokal modell-selektor per kampanj
  const [templateId, setTemplateId] = useState('');  // tom = ingen mall (rekommenderas att välja)
  const [templates, setTemplates] = useState([]);
  const [useProductData, setUseProductData] = useState(false);
  const [partNos, setPartNos] = useState(['', '', '', '', '', '']);
  const [resolvedProducts, setResolvedProducts] = useState(null);
  const [regenMarket, setRegenMarket] = useState(null);
  const [content, setContent] = useState(null);
  const [activeMarket, setActiveMarket] = useState('SE');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleMarket = (code) => {
    setForm(f => ({
      ...f,
      markets: f.markets.includes(code)
        ? f.markets.filter(m => m !== code)
        : [...f.markets, code],
    }));
  };

  const filledPartNos = partNos.map(p => p.trim()).filter(Boolean);

  const generate = async () => {
    if (!form.campaign_name.trim() || !form.brief.trim()) {
      toast.error('Kampanjnamn och beskrivning krävs');
      return;
    }
    if (form.markets.length === 0) {
      toast.error('Välj minst en marknad');
      return;
    }
    if (useProductData && filledPartNos.length === 0) {
      toast.error('Fyll i minst ett PartNo eller stäng av "Använd produktdata"');
      return;
    }
    setLoading(true);
    setResolvedProducts(null);
    try {
      const r = await fetch(`${API_URL}/api/newsletter/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id, access_token: accessToken,
          model, template_id: templateId || null,
          part_nos: useProductData ? filledPartNos : [],
          ...form,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setContent(data.generated_content || {});
      setResolvedProducts(data.resolved_products || null);
      setActiveMarket(form.markets[0]);
      if (data.usage) {
        // Split tokens evenly across requested markets so each marknad får sin egen log-rad
        // (annars hamnar allt på en sammanslagen "SE,NO,FI,DK,DE"-rad i kostnadsdashboarden).
        const n = Math.max(1, form.markets.length);
        const inPer  = Math.floor((data.usage.inputTokens  || 0) / n);
        const outPer = Math.floor((data.usage.outputTokens || 0) / n);
        // Lägg ev. rest på första marknaden så summan blir korrekt.
        const inRem  = (data.usage.inputTokens  || 0) - inPer  * n;
        const outRem = (data.usage.outputTokens || 0) - outPer * n;
        form.markets.forEach((mkt, i) => {
          addUsageLog({
            model:        data.usage.model,
            inputTokens:  inPer  + (i === 0 ? inRem  : 0),
            outputTokens: outPer + (i === 0 ? outRem : 0),
            market:       mkt,
            category:     null,
            source:       'newsletter',
            module:       'newsletter',
          });
        });
      }
      toast.success('Nyhetsbrev genererat');
    } catch (e) {
      toast.error(`Generering misslyckades: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const regenerateMarket = async (mkt) => {
    setRegenMarket(mkt);
    try {
      const r = await fetch(`${API_URL}/api/newsletter/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id, access_token: accessToken,
          model, template_id: templateId || null,
          part_nos: useProductData ? filledPartNos : [],
          ...form,
          markets: [mkt], market: mkt,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || r.status);
      const data = await r.json();
      setContent(c => ({ ...(c || {}), [mkt]: data.generated_content[mkt] }));
      if (data.usage) {
        addUsageLog({
          model:        data.usage.model,
          inputTokens:  data.usage.inputTokens || 0,
          outputTokens: data.usage.outputTokens || 0,
          market:       mkt,
          category:     null,
          source:       'newsletter',
          module:       'newsletter',
        });
      }
      toast.success(`${mkt} regenererad`);
    } catch (e) {
      toast.error(`Regenerering misslyckades: ${e.message}`);
    } finally {
      setRegenMarket(null);
    }
  };

  const updateField = (mkt, field, value) => {
    setContent(c => ({ ...(c || {}), [mkt]: { ...((c || {})[mkt] || {}), [field]: value } }));
  };

  const copyAll = (mkt) => {
    const c = (content || {})[mkt] || {};
    const text = FIELDS
      .map(f => c[f.key] ? `${f.label}: ${c[f.key]}` : null)
      .filter(Boolean)
      .join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Kopierat till urklipp');
  };

  const saveDraft = async () => {
    try {
      const r = await fetch(`${API_URL}/api/newsletter/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id, access_token: accessToken, ...form,
          generated_content: content,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || r.status);
      toast.success('Utkast sparat');
    } catch (e) {
      toast.error(`Sparning misslyckades: ${e.message}`);
    }
  };

  const visibleMarkets = useMemo(
    () => form.markets.filter(m => content && content[m]),
    [form.markets, content]
  );

  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' };

  return (
    <div data-testid="nl-create-tab" style={{ display: 'grid', gap: 28 }}>
      <section className="dash-card" style={{ padding: 28 }}>
        <h2 className="dash-card-title" style={{ marginBottom: 20 }}>Brief</h2>

        <div style={{ display: 'grid', gap: 18 }}>
          {/* 1. Mall (första valet — uppmuntra starkt) */}
          <div
            data-testid="nl-template-block"
            style={{
              padding: 16, borderRadius: 12,
              background: templateId ? 'rgba(228, 32, 93, 0.05)' : '#fefce8',
              border: `1px solid ${templateId ? 'rgba(228, 32, 93, 0.25)' : '#fde68a'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <FileText size={16} style={{ color: templateId ? 'var(--accent)' : '#a16207' }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                Mall {templateId ? '' : '— rekommenderas'}
              </span>
            </div>
            <select
              data-testid="nl-template-select"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={loading}
              className="field-input field-select"
              style={{ width: '100%' }}
            >
              <option value="">Ingen mall (skriv fritt brief nedan)</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>
              Mallen styr struktur och vinkel för kampanjen (t.ex. produktlansering, säsongskampanj, deal). Brand voice är alltid samma.
            </p>
          </div>

          {/* 2. Kampanjnamn */}
          <label style={{ display: 'block' }}>
            <div style={labelStyle}>Kampanjnamn</div>
            <input
              type="text"
              value={form.campaign_name}
              onChange={(e) => set('campaign_name', e.target.value)}
              placeholder="t.ex. Beez Remedy lansering v.20"
              data-testid="nl-input-name"
              className="field-input"
            />
          </label>

          {/* 3. Kampanjbeskrivning */}
          <label style={{ display: 'block' }}>
            <div style={labelStyle}>Kampanjbeskrivning</div>
            <textarea
              rows={5}
              value={form.brief}
              onChange={(e) => set('brief', e.target.value)}
              placeholder="Beskriv kampanjen i fritext. Inkludera kategori, varumärke, erbjudande, produktdetaljer och andra relevanta detaljer.&#10;t.ex. Lansering av Beez Remedy sneakers i två nya färger. 20% rabatt med kod REMEDY20. Gäller 48h. Skor-kategori, sommarlansering."
              data-testid="nl-input-brief"
              className="field-input field-textarea"
              style={{ resize: 'vertical' }}
            />
          </label>

          {/* Produktdata — toggle + 6 PartNo-inputs */}
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 12, padding: 16,
          }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              userSelect: 'none',
            }}>
              <span
                role="switch"
                aria-checked={useProductData}
                onClick={(e) => { e.preventDefault(); setUseProductData(v => !v); }}
                data-testid="nl-toggle-product-data"
                style={{
                  width: 38, height: 22, borderRadius: 11,
                  background: useProductData ? 'var(--accent)' : '#cbd5e1',
                  position: 'relative', transition: 'background 0.15s',
                  flexShrink: 0,
                }}
              >
                <span style={{
                  position: 'absolute', top: 2, left: useProductData ? 18 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.15s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>
                  Använd produktdata
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  Hämta produktnamn och beskrivning från Norce per marknad (max 6 produkter).
                  AI:n använder dessa fakta istället för att gissa.
                </div>
              </div>
            </label>

            {useProductData && (
              <div
                data-testid="nl-partnos-grid"
                style={{
                  marginTop: 14,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 10,
                }}
              >
                {partNos.map((pn, i) => (
                  <div key={i}>
                    <label className="field-label" style={{ fontSize: 12 }}>
                      PartNo {i + 1}
                    </label>
                    <input
                      type="text"
                      value={pn}
                      onChange={(e) => {
                        const next = [...partNos];
                        next[i] = e.target.value;
                        setPartNos(next);
                      }}
                      placeholder={i === 0 ? '10001748-1077-10047' : ''}
                      data-testid={`nl-partno-${i}`}
                      className="field-input"
                      style={{ fontFamily: 'monospace', fontSize: 13 }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div style={labelStyle}>Marknader</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {MARKETS.map(m => {
                const checked = form.markets.includes(m.code);
                return (
                  <button
                    key={m.code}
                    type="button"
                    data-testid={`nl-market-${m.code}`}
                    onClick={() => toggleMarket(m.code)}
                    style={{
                      cursor: 'pointer',
                      padding: '8px 14px', borderRadius: 999,
                      border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                      background: checked ? 'rgba(228, 32, 93, 0.12)' : 'transparent',
                      color: checked ? 'var(--text)' : 'var(--muted)',
                      fontSize: 13, fontWeight: 500,
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <span>{m.flag}</span> {m.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div style={labelStyle}>Ton</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, padding: '8px 0' }}>
              Vårdväskans B2C-brand-voice används automatiskt för all copy (varm, peppig, glimten i ögat).
              Tonen adapteras idiomatiskt per marknad.
            </p>
          </div>

          <label style={{ display: 'block' }}>
            <div style={labelStyle}>CTA-text (default)</div>
            <input
              type="text"
              value={form.cta_text}
              onChange={(e) => set('cta_text', e.target.value)}
              data-testid="nl-input-cta"
              className="field-input"
              style={{ maxWidth: 280 }}
            />
          </label>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <label
              data-testid="nl-model-selector-wrapper"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', background: '#f8fafc',
                border: '1px solid #e2e8f0', borderRadius: 10,
              }}
            >
              <Cpu size={14} style={{ color: '#64748b' }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Modell:</span>
              <select
                data-testid="nl-model-select"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={loading}
                style={{
                  background: 'transparent', border: 'none', outline: 'none',
                  fontSize: 13, fontWeight: 600, color: '#0f172a',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {MODEL_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
            <button
              onClick={generate}
              disabled={loading}
              data-testid="nl-generate-btn"
              className={`generate-btn${!loading ? ' generate-btn--active' : ''}`}
              style={{ width: 'auto', minWidth: 240 }}
            >
              {loading
                ? <><Loader2 size={16} className="ap-progress-spin" /> Genererar...</>
                : <><Sparkles size={16} /> Generera nyhetsbrev</>}
            </button>
          </div>
        </div>
      </section>

      {content && (
        <section className="dash-card" style={{ padding: 28 }} data-testid="nl-results">
          {resolvedProducts && Object.keys(resolvedProducts).length > 0 && (
            <div data-testid="nl-resolved-products" style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.05 }}>
                Hämtad produktdata från Norce
              </div>
              {(() => {
                // Identifiera PartNos som saknas i ALLA marknader → mjuk varning
                const allMarkets = Object.keys(resolvedProducts);
                const missingAcrossAll = filledPartNos.filter(pn =>
                  allMarkets.every(m =>
                    !(resolvedProducts[m] || []).some(it => it.part_no === pn && it.found)
                  )
                );
                return missingAcrossAll.length > 0 ? (
                  <div style={{
                    background: '#fef3c7', color: '#92400e',
                    border: '1px solid #fde68a', borderRadius: 8,
                    padding: '8px 12px', marginBottom: 12, fontSize: 12,
                  }}>
                    <strong>Obs:</strong> {missingAcrossAll.length} PartNo hittades inte i någon av valda marknader:{' '}
                    {missingAcrossAll.map(p => <code key={p} style={{ marginRight: 6 }}>{p}</code>)}
                  </div>
                ) : null;
              })()}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 10,
              }}>
                {Object.entries(resolvedProducts).map(([market, items]) => (
                  <div
                    key={market}
                    data-testid={`nl-resolved-${market}`}
                    style={{
                      background: '#f8fafc', border: '1px solid #e2e8f0',
                      borderRadius: 10, padding: 12,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                      {(MARKETS.find(m => m.code === market) || {}).flag} {market}
                    </div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 12 }}>
                      {items.map((it, idx) => (
                        <li key={idx} style={{ marginBottom: 6, color: it.found ? '#0f172a' : '#94a3b8' }}>
                          <span style={{ color: it.found ? '#10b981' : '#dc2626', marginRight: 6 }}>
                            {it.found ? '✓' : '✗'}
                          </span>
                          <code style={{ fontSize: 11, color: '#64748b' }}>{it.part_no}</code>
                          {it.found
                            ? <div style={{ marginLeft: 18, marginTop: 2, fontSize: 12 }}>{it.name}</div>
                            : <span style={{ marginLeft: 6, fontSize: 11 }}>(ej hittad)</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {visibleMarkets.map(mkt => {
              const m = MARKETS.find(x => x.code === mkt);
              return (
                <button
                  key={mkt}
                  data-testid={`nl-market-tab-${mkt}`}
                  onClick={() => setActiveMarket(mkt)}
                  className={`tab-btn${activeMarket === mkt ? ' tab-btn--active' : ''}`}
                >
                  {m?.flag} {m?.name}
                </button>
              );
            })}
          </div>

          {visibleMarkets.includes(activeMarket) && (
            <NewsletterMarketEditor
              key={activeMarket}
              market={activeMarket}
              content={(content || {})[activeMarket] || {}}
              onChange={(field, val) => updateField(activeMarket, field, val)}
              onCopy={() => copyAll(activeMarket)}
              onRegenerate={() => regenerateMarket(activeMarket)}
              regenerating={regenMarket === activeMarket}
            />
          )}

          <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={saveDraft}
              data-testid="nl-save-draft"
              className="ap-btn-pill"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px' }}
            >
              <Save size={14} /> Spara utkast
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
